import { create } from 'zustand';

import { bridgeNameSchema } from '@/lib/api/speed';
import type { BridgeName } from '@/lib/api/speed';

/**
 * The testing switch behind the small gray gear beside the deposit and withdraw
 * buttons: pins the bridge a transfer is quoted and executed on instead of the
 * backend's automatic choice (the registry's per-chain order, typically Relay,
 * then LI.FI, then deBridge, the next one only when the previous cannot serve
 * the route). With Relay healthy on every chain there is otherwise no way to
 * exercise a LI.FI or deBridge leg end to end.
 *
 * 'auto' sends nothing and keeps the ordinary behaviour. A pinned bridge is
 * sent as `bridge` on the quote and the start calls; the backend quotes that
 * bridge only, with no fallback, so what is being tested is exactly what would
 * run on the day the other bridge is down.
 *
 * Remembered per browser so a tester does not re-pin it on every reload.
 */
export type BridgeOverride = 'auto' | BridgeName;

const STORAGE_KEY = 'snappy.bridgeOverride';

function readStored(): BridgeOverride {
    try {
        const parsed = bridgeNameSchema.safeParse(
            window.localStorage.getItem(STORAGE_KEY),
        );
        return parsed.success ? parsed.data : 'auto';
    } catch {
        return 'auto';
    }
}

function writeStored(value: BridgeOverride): void {
    try {
        if (value === 'auto') {
            window.localStorage.removeItem(STORAGE_KEY);
        } else {
            window.localStorage.setItem(STORAGE_KEY, value);
        }
    } catch {
        // storage unavailable: the choice still holds for this page
    }
}

interface BridgeOverrideState {
    bridge: BridgeOverride;
    setBridge: (bridge: BridgeOverride) => void;
}

export const useBridgeOverride = create<BridgeOverrideState>((set) => ({
    bridge: readStored(),
    setBridge: (bridge) => {
        writeStored(bridge);
        set({ bridge });
    },
}));

/** The request member a pinned bridge travels as; nothing when automatic. */
export function bridgeRequestField(
    bridge: BridgeOverride = useBridgeOverride.getState().bridge,
): { bridge?: BridgeName } {
    return bridge === 'auto' ? {} : { bridge };
}
